import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { UsersApiAdapter } from '../adapters/UsersApiAdapter';
import { Service } from '../services/Service';

const PENDING_INVITATIONS_QUERY_KEY = ['pending-invitations'] as const;

// 🎯 Flag global para marcar cuando se está cancelando una invitación
// Esto evita que se detecte como una aceptación
let isCancellingInvitation = false;

/**
 * Hook para cancelar una invitación
 */
export const useCancelInvitation = () => {
  const { t } = useTranslation('usersModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const httpClient = createHttpClient(apiClientWH);
  const usersAdapter = new UsersApiAdapter(httpClient);
  const usersService = new Service(usersAdapter);

  return useMutation({
    mutationFn: (invitationId: number) => usersService.cancelInvitation(invitationId),
    onMutate: () => {
      // 🎯 Marcar que se está cancelando antes de invalidar la query
      // Esto evita que se detecte como una aceptación
      isCancellingInvitation = true;
      if (typeof globalThis !== 'undefined') {
        globalThis.__isCancellingInvitation = true;
      }
    },
    onSuccess: () => {
      // Invalidar query de invitaciones pendientes para refrescar la lista
      queryClient.invalidateQueries({ queryKey: PENDING_INVITATIONS_QUERY_KEY });

      // 🎯 Resetear el flag después de un breve delay para permitir que se actualice la lista
      setTimeout(() => {
        isCancellingInvitation = false;
        if (typeof globalThis !== 'undefined') {
          globalThis.__isCancellingInvitation = false;
        }
      }, 1000);

      // Mostrar toast de éxito
      toast({
        title: t('invitations.success.cancel', 'Invitación cancelada exitosamente'),
        description: t(
          'invitations.success.cancelDesc',
          'La invitación ha sido cancelada y el usuario ya no podrá aceptarla'
        ),
        variant: 'default',
      });
    },
    onError: (error: any) => {
      console.error('Error al cancelar invitación:', error);

      // Extraer mensaje de error
      let errorMessage = t('invitations.errors.cancelFailed', 'Error al cancelar invitación');

      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      // 🎯 Mapear mensajes específicos del backend a traducciones
      // Mensaje cuando se intenta cancelar una invitación ya aceptada
      if (
        errorMessage.includes('Cannot cancel an already accepted invitation') ||
        errorMessage.includes('cannot cancel an already accepted invitation') ||
        errorMessage.includes('ya aceptada') ||
        errorMessage.includes('already accepted')
      ) {
        errorMessage = t('invitations.errors.alreadyAccepted', 'Esta invitación ya fue aceptada');
      }
      // Mensaje cuando la invitación no se encuentra
      else if (errorMessage.includes('no encontrada') || errorMessage.includes('not found')) {
        errorMessage = t('invitations.errors.notFound', 'Invitación no encontrada');
      }
      // Mensaje cuando no hay permisos
      else if (errorMessage.includes('permisos') || errorMessage.includes('permission')) {
        errorMessage = t(
          'invitations.errors.noPermission',
          'No tienes permisos para realizar esta acción'
        );
      }

      toast({
        title: t('invitations.errors.cancelFailed', 'Error al cancelar invitación'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};


